import type { WhiteboardTranslations } from "./translations";

export const ZH_TW_TRANSLATIONS: WhiteboardTranslations = {
  workspace: {
    cancelLabel: "取消",
    syncingSession: "正在同步課堂工作階段...",
    initializingBoard: "正在初始化白板...",
    clearSlideModalTitle: "清空目前頁面板書？",
    clearSlideModalDescription: "此操作將清除目前投影片頁面上的所有板書內容。",
    clearSlideConfirmLabel: "清空目前頁面",
    slideClearedToastTitle: "已清空目前頁面",
    slideClearedToastDesc: "目前頁面上的所有板書已清空。",
    slowNetworkAria: "慢速網路連線提示",
    slowNetworkTitle: "網路連線較慢",
    slowNetworkDesc: "— 正在同步線上課堂工作階段，目前處於本機草稿模式。",
    retrySyncButton: "重試同步",
    toolErrorTitle: "白板發生問題",
    toolErrorFallback: "執行此工具時發生非預期的錯誤。",
    toolReloadButton: "重新載入白板",
    dismissToastAria: "關閉通知",
  },
  hud: {
    appMenuAriaLabel: "應用選單",
    boardMenuAriaLabel: "白板選單",
    exportPng: "匯出圖片 (PNG)",
    exportPdf: "匯出講義 (PDF)",
    pdfScopeAriaLabel: "PDF 匯出範圍",
    pdfScopeAll: "全部課程",
    pdfScopeTab: "目前白板分頁",
    pdfScopeSlide: "目前頁面",
    findOnCanvas: "在畫布中尋找",
    help: "說明",
    clearSlide: "清空目前頁面",
    paperStyleSectionTitle: "紙張與練字格 (Paper Style)",
    strokeSmoothingSectionTitle: "畫筆平滑度",
    canvasBgSectionTitle: "畫布背景色",
    canvasBgSwatchAria: (color: string) => `畫布背景 ${color}`,
    customBgColorAria: "自訂背景色",
    paperPresets: {
      blank: "空白",
      "grid-small": "小方格",
      "grid-large": "大方格",
      dots: "點陣紙",
      lined: "橫線紙",
      tianzige: "田字格",
      mizige: "米字格",
      "pinyin-tianzige": "拼音田字格",
    },
    prevSlideAria: "上一頁",
    nextSlideAria: "下一頁",
    addSlideAria: "新增頁面",
    duplicateSlideAria: "複製頁面",
    duplicateSlideTitle: "複製目前頁面",
    deleteSlideAria: "刪除目前頁面",
    confirmDeleteSlideAria: "確認刪除頁面",
    confirmDeleteSlideLabel: "確認刪除？",
    cancelDeleteSlideAria: "取消刪除頁面",
    cancelLabel: "取消",
    drawingToolbarAria: "繪圖與教學素材工具列",
    pointerSelectionLabel: "選取指標",
    pointerHandLabel: "抓手移動畫布",
    shapesMenuAria: "選擇幾何圖形",
    shapeTools: {
      rectangle: "矩形",
      diamond: "菱形",
      ellipse: "橢圓",
      arrow: "箭頭",
      line: "直線",
    },
    freedrawAria: "畫筆",
    freedrawTitle: "自由畫筆 (P)",
    textAria: "文字",
    textTitle: "文字 (T)",
    eraserAria: "橡皮擦",
    eraserTitle: "橡皮擦 (E)",
    insertMenuTriggerAria: "插入圖片、文件與影片",
    insertMenuTriggerTitle: "插入圖片、PDF 或影片",
    insertPopoverAria: "插入教學素材與多媒體",
    insertImageLabel: "插入圖片",
    pinyinAssistantTrigger: "拼音與漢字助手",
    stemTriggerAria: "STEM、數學與圖形庫工具",
    stemTriggerTitle: "LaTeX 公式、互動模擬與 STEM 圖庫",
    stemPopoverAria: "數學、模擬與 STEM 圖庫工具",
    stemLibraryTrigger: "教學素材與筆畫圖庫",
    stemAllSubjects: "全部",
    stemGenerateSectionTitle: "按需產生",
    stemSidebarTitle: "圖庫",
    stemSidebarCloseAria: "關閉圖庫",
    stemCategoryLabels: {
      physics: { label: "物理", description: "電路圖、光學、力學圖示" },
      math: { label: "數學", description: "平面與立體幾何、座標系、函數" },
      chemistry: { label: "化學", description: "實驗儀器、分子結構模型" },
      cs: { label: "資訊與流程圖", description: "流程圖、網路拓撲、系統架構" },
      chinese: {
        label: "中文教學 (田字格/筆畫)",
        description: "田字格、米字格、永字八法基本筆畫、漢字結構與聲調符號",
      },
    },
    latexTrigger: "插入數學公式 (LaTeX)",
    simulationTrigger: "圖形與幾何",
    youtubeTrigger: "插入 YouTube 影片",
    laserAria: "雷射筆",
    laserTitle: "雷射筆 (K)",
    cursorChatTriggerAria: "游標即時聊天",
    cursorChatPlaceholder: "輸入游標訊息...",
    cursorChatHint: "Enter 固定 · Esc 關閉",
    remoteHostBadge: "教師",
    remoteOverflowBadge: (count: number) => `+${count} 人`,
    teacherPermissionsAria: "學生互動設定",
    screenFollowYou: "跟隨你的畫面",
    screenFreeMove: "自由移動",
    screenFollowingTeacher: "正在跟隨教師",
    drawCustom: "指定學生",
    drawViewOnly: "僅檢視",
    undoAria: "復原",
    undoTitle: "復原 (Ctrl+Z)",
    redoAria: "重做",
    redoTitle: "重做 (Ctrl+Y)",
    zoomOutAria: "縮小",
    zoomInAria: "放大",
    zoomMenuAria: "檢視縮放選項",
    zoomMenuHeader: (percent: number) => `畫布檢視 (${percent}%)`,
    fitSlide: "適應整塊畫板",
    fitWidth: "適應畫板寬度",
    zoom100Label: "縮放 100% (1:1)",
    pageNumberInputAria: "輸入頁碼",
  },
  permissions: {
    teacherDialogTitle: "學生互動設定",
    teacherScreenSection: "學生畫面模式",
    followYouBtn: "跟隨你的畫面",
    freeMoveBtn: "自由移動",
    teacherFollowHint: "學生將始終看到您目前瀏覽的畫布區域，無法自行平移視角。",
    teacherFreeHint: "學生可以在畫布上自由縮放和平移視角。",
    drawingPermissionSection: "學生畫板權限",
    viewOnlyMode: "僅檢視",
    customMode: "指定學生",
    viewOnlyHint: "僅您可以繪圖，學生處於唯讀觀看模式。",
    customModeHint: "選擇可以繪圖的學生。",
    searchParticipantPlaceholder: "搜尋參與者",
    selectedCount: (editable: number, total: number) => `已選擇: ${editable}/${total}`,
    deselectAllBtn: "取消全選",
    selectStudentAria: (name: string) => `選擇 ${name}`,
  },
  pinyinModal: {
    title: "拼音與漢字生字卡助手",
    subtitle:
      "輸入帶數字聲調的拼音（如 ni3 hao3、lv4）自動轉換為聲調符號，並一鍵產生田字格/米字格生字卡片。",
    modeCardLabel: "拼音 + 漢字練字格卡片 (田字格/米字格)",
    modeCardDesc: "在畫布中央產生可自由移動、縮放的拼音四線格與漢字田字格組合卡片",
    modeTextLabel: "僅插入拼音 / 漢字文字",
    modeTextDesc: "在畫布中央直接插入帶聲調的拼音與漢字文字元素",
    pinyinInputLabel: "拼音輸入（支援數字 1-5 自動轉聲調，v 或 u: 轉 ü）",
    pinyinInputPlaceholder: "例如：ni3 hao3, zhong1 wen2, lv4 se4...",
    convertedPreviewLabel: "聲調轉換預覽",
    hanziInputLabel: "對應漢字（每字一格）",
    hanziInputPlaceholder: "例如：你好、中文...",
    gridStyleLabel: "練字格樣式",
    gridTianzige: "田字格",
    gridMizige: "米字格",
    toneVowelsTitle: "聲調韻母快捷輸入（點擊插入）",
    punctuationTitle: "中文標點符號",
    colorLabel: "字跡顏色",
    strokeColorLabels: {
      ink: "墨黑",
      white: "純白",
      crimson: "緋紅",
      blue: "寶藍",
      emerald: "翠綠",
      amber: "琥珀黃",
    },
    copyPinyinButton: "複製拼音",
    copiedPinyinButton: "已複製！",
    cancelButton: "取消",
    insertCardButton: "插入生字卡片",
    insertTextButton: "插入拼音文字",
    practiceTitle: "課堂練習模式",
    practiceFull: "完整示範 (拼音+漢字)",
    practiceTrace: "描紅練字 (示範+描紅+空格)",
    practiceDictation: "聽寫默寫 (僅顯示拼音)",
    practicePinyinQuiz: "看字注音 (僅顯示漢字)",
    gridJiugongge: "九宮格 (9-Palace)",
    gridSikexian: "四線三格 (純拼音)",
    presetsTitle: "常用教學詞彙、基本筆畫與偏旁部首",
    cellsCountBadge: (count: number) => `${count} 格`,
  },
  modals: {
    cancel: "取消",
    close: "關閉",
    confirm: "確認",
    latexEditTitle: "編輯數學與科學公式 (LaTeX)",
    latexCloseTitle: "關閉對話框 (Esc)",
    latexChipInsertTitle: (latex: string) => `插入 ${latex}`,
    latexChips: {
      systemCases: "聯立方程組",
      orBrackets: "或 (方括號)",
      derivativePrime: "導數 f'(x)",
      derivative: "微分 d/dx",
      partialDerivative: "偏導數",
      integral: "定積分",
      limit: "極限",
      sum: "連加總和",
      matrix2x2: "2x2 矩陣",
      iff: "⇔ 等價",
      implies: "⇒ 推導",
      inSet: "∈ 屬於",
      realSet: "ℝ 實數集",
      vector: "向量 AB",
      combination: "組合 C_n^k",
      subscript: "下標",
      delta: "Δ Delta",
      alpha: "α Alpha",
      beta: "β Beta",
      lambda: "λ Lambda",
      plusMinus: "± 正負",
      approx: "≈ 約等於",
      infinity: "∞ 無窮大",
    },
    latexInkColors: {
      defaultDark: "預設 (自適應白色)",
      defaultLight: "預設 (黑色)",
      amber: "琥珀黃",
      blue: "天藍",
      emerald: "翠綠",
      rose: "玫紅",
      purple: "紫羅蘭",
    },
    latexInputPlaceholder: "例如: \\frac{-b \\pm \\sqrt{b^2 - 4ac}}{2a}",
    latexSyntaxWarning: "語法警告",
    latexEmptyPreviewHint: "在上方輸入 LaTeX 公式以預覽",
    latexInkColorLabel: "墨水顏色:",
    latexInsertingButton: "正在插入...",
    latexUpdateButton: "更新公式",
    latexInsertButton: "插入公式",
    simulationTitle: "函數圖形與平面幾何",
    simulationSubtitle: "選擇直角座標系圖形畫板或平面幾何畫板插入至課堂頁面",
    simulationInsertButton: "插入至白板",
    studios: {
      graphing: {
        title: "函數圖形與微積分",
        subtitle: "Oxy 直角座標系",
        description:
          "在 Oxy 座標平面繪製函數圖形、不等式可行域、圓與橢圓。支援參數滑桿、切線、積分面積著色以及零點與極值分析。",
        tags: ["函數圖形", "不等式", "參數滑桿", "切線分析", "極值與零點"],
      },
      geometry: {
        title: "平面幾何",
        subtitle: "直覺作圖與動態拖曳",
        description:
          "提供點、線段、垂線、平行線、圓、多邊形及教材標準幾何圖形範本，支援直接拖曳頂點觀察幾何關係。",
        tags: ["點與線段", "垂線與平行線", "圓", "多邊形", "教材圖形"],
      },
    },
    youtubeTitle: "插入 YouTube 影片",
    youtubeSubtitle: "嵌入支援全班同步播放控制的 YouTube 影片",
    youtubeUrlLabel: "YouTube 連結",
    youtubeUrlPlaceholder: "https://www.youtube.com/watch?v=... 或 https://youtu.be/...",
    youtubeInvalidUrlError: "連結格式無效。支援 watch、youtu.be、shorts、embed 格式。",
    youtubeDisplayTitleLabel: "顯示標題",
    youtubePreviewLabel: "預覽 (16:9)",
    youtubeStartTimeBadge: (seconds: number) => `起始: ${seconds}s`,
    youtubeInsertButton: "插入至白板",
    videoUploadTitle: "上傳本機教學影片",
    videoUnsupportedError: "僅支援 MP4、WebM 影片（最大 250MB）",
    videoPresignFailedError: (status: number) => `無法初始化上傳 (${status})`,
    videoUploadHttpFailedError: (status: number) => `上傳至儲存伺服器失敗 (HTTP ${status})`,
    videoUploadNetworkError: "上傳影片至儲存伺服器時發生網路錯誤",
    mermaidTitle: "Mermaid 轉教學圖表",
    mermaidSubtitle: "在白板上直接產生流程圖、循序圖或架構圖",
    mermaidCloseAria: "關閉",
    mermaidTemplatesLabel: "範本：",
    mermaidTemplateFlowchart: "流程圖 (Flowchart)",
    mermaidTemplateSequence: "循序圖 (Sequence)",
    mermaidTemplateClass: "類別圖 (Class)",
    mermaidSyntaxLabel: "Mermaid 語法：",
    mermaidKeyboardHint: "按 Ctrl + Enter 插入",
    mermaidEmptyError: "請輸入 Mermaid 語法。",
    mermaidNoElementsError: "未能從此圖表定義產生圖形元素。",
    mermaidSyntaxError: (msg: string) => `Mermaid 語法錯誤: ${msg}`,
    mermaidSupportedDiagramsHint: "支援流程圖、循序圖、類別圖與狀態圖",
    mermaidCancelButton: "取消",
    mermaidProcessingButton: "正在處理...",
    mermaidInsertButton: "插入畫板",
    webEmbedTitle: "嵌入學習網頁",
    webEmbedSubtitle: "在畫布中嵌入互動式網頁或模擬實驗",
    webEmbedCloseAria: "關閉",
    webEmbedUrlLabel: "網頁連結 (URL https://...)",
    webEmbedUrlPlaceholder: "https://example.com/interactive-app",
    webEmbedNameLabel: "顯示名稱（選填）",
    webEmbedNamePlaceholder: "嵌入內容",
    webEmbedInvalidUrlError: "連結無效，請輸入以 https:// 開頭的網址",
    webEmbedCancelButton: "取消",
    webEmbedInsertButton: "插入畫板",
    mathDockPlotToGraphTitle: "在函數圖表板上繪製此表示式（若無則自動新建）",
    mathDockPlotToGraphButton: "繪製圖表",
    mathDockStampToBoardTitle: "將此數學公式貼到白板上",
    mathDockStampToBoardButton: "貼到白板",
    mathDockCollapseTitle: "折疊為迷你控制列",
    mathDockExpandTitle: "展開完整數學工具箱與教材範例",
    mathDockCollapseButton: "收起",
    mathDockExpandButton: "展開",
    mathDockDoneButton: "完成",
    mathDockTabAlgebra: "123 代數與數",
    mathDockTabFunctions: "f(x) 三角與指數",
    mathDockTabPresets: "★ 教材典型範例",
    mathDockTabCalculus: "∫∑ 微積分與 LaTeX",
    mathDockTemplates: {
      cubic_m: "三次函數（含參數 m）",
      biquadratic_m: "雙二次函數",
      rational: "有理分式函數（漸近線）",
      parabola_m: "二次拋物線",
      trig_wave: "三角函數波形",
      radical: "根式函數",
      linear_ineq: "二元一次不等式區域",
      circle: "圓的方程式",
      ellipse: "標準橢圓方程式",
    },
  },
  widgets: {
    modeToggleToAnnotateTitle: "切換至批註繪製模式",
    modeToggleToInteractTitle: "切換至互動操作模式",
    interactLabel: "互動",
    annotateLabel: "批註",
    unlockWidgetTitle: "畫板已鎖定（點擊解鎖）",
    lockWidgetTitle: "鎖定畫板位置",
    lockedByTeacherTitle: "教師已鎖定此畫板",
    maximizeWidgetTitle: "最大化適配螢幕",
    restoreWidgetTitle: "還原先前的視角",
    confirmDeleteWidgetTitle: "確認刪除此畫板",
    confirmDeleteWidgetLabel: "確認刪除？",
    cancelDeleteWidgetTitle: "取消刪除",
    cancelDeleteWidgetLabel: "取消",
    deleteWidgetAria: "刪除畫板",
    resizeHandleTitle: "拖曳調整大小",
    microPromptTouchPoint: "1. 點擊基準線段或經過的點",
    microPromptPassThroughPoint: "2. 點擊任意點或空白位置作線",
    microPromptPerpendicularEdge: "2. 點擊目標線段作垂線",
    microPromptParallelEdge: "2. 點擊目標線段作平行線",
    microPromptCancelTitle: "取消操作 (Esc)",
    openSlidersTitle: "展開參數滑桿面板",
    parametersLabel: "參數",
    collapseSlidersTitle: "收起參數面板",
    collapseLabel: "收起",
    videoIndependentTitle: "目前為自主觀看模式 - 點擊同步跟隨教師",
    videoFollowingTitle: "目前跟隨教師同步播放 - 點擊自主觀看",
    videoIndependentLabel: "自主觀看",
    videoFollowTeacherLabel: "跟隨教師",
    videoResyncButton: "恢復跟隨教師",
    videoResyncTitle: "恢復與教師同步播放",
    hidePlotTitle: "隱藏函數圖形",
    showPlotTitle: "顯示函數圖形",
    hideInequalityTitle: "隱藏可行域",
    showInequalityTitle: "顯示可行域",
    hideConicTitle: "隱藏圓錐曲線",
    showConicTitle: "顯示圓錐曲線",
    inequalityShortTag: "不等式:",
    deleteExpressionTitle: (label: string) => `刪除 ${label}`,
    invalidSyntaxLabel: "語法無效",
    mobileExpressionListTitle: "表示式與微積分列表",
    addButtonLabel: "新增",
    openMobileFormulaSheetTitle: "點擊開啟公式面板與觸控鍵盤",
    emptyExpressionLabel: "(空)",
    openAlgebraDrawerTitle: "開啟代數面板（表示式列表）",
    expressionListTitle: "表示式列表",
    collapseAlgebraDrawerTitle: "收起代數面板 [ ◀ ]",
    omniInputPlaceholder: "輸入函數、不等式、圓或橢圓...",
    omniInputAria: "新增表示式",
    clickToAddExpression: "點擊輸入函數 / 不等式...",
    newBadge: "+ 新增",
    addPointLabel: "+ 點",
    triangleAbcLabel: "三角形 ABC",
    triangleLabel: "三角形",
    segmentLabel: "線段",
    lineLabel: "直線",
    vectorLabel: "向量",
    perpendicularLabel: "垂線",
    parallelLabel: "平行線",
    circleLabel: "圓",
    polygonLabel: "多邊形",
    clearGeometryBoardTitle: "清空幾何畫板",
    geometryPresets: {
      tri_altitudes: "三角形與三條高線",
      tri_medians: "三角形與三條中線",
      tri_circum: "三角形與外接圓",
      tri_in: "三角形與內切圓",
      tri_right: "直角三角形",
      tri_iso: "等腰三角形",
      tri_eq: "正三角形",
      quad_para: "平行四邊形",
      quad_rect: "矩形",
      quad_rhomb: "菱形",
      quad_sq: "正方形",
      circle_tan: "圓與兩條切線",
      quad_inscribed: "圓內接四邊形",
      poly_pentagon: "正五邊形",
      quad_trapezoid: "等腰梯形",
    },
    extremumMaxBadge: (x: string, y: string) => `極大(${x}, ${y})`,
    extremumMinBadge: (x: string, y: string) => `極小(${x}, ${y})`,
    rootSweeperBadge: (paramName: string, val: string, count: number) =>
      `${paramName} = ${val} → ${count} 個根`,
    lpUnboundedReasons: {
      both_unbounded: "可行域無界 - 不存在最大值與最小值",
      max_unbounded: "不存在最大值（因可行域無界趨向 +∞）",
      min_unbounded: "不存在最小值（因可行域無界趨向 -∞）",
    },
    lpTableHeader: (fnExpr: string) => `最佳化 F(x, y) = ${fnExpr}`,
    lpVertexCol: "頂點",
    lpCoordsCol: "座標",
    lpMaxSummary: (val: string, label: string, x: string, y: string) =>
      `★ max F = ${val} 於 ${label} (${x}, ${y})`,
    lpMinSummary: (val: string, label: string, x: string, y: string) =>
      `★ min F = ${val} 於 ${label} (${x}, ${y})`,
    stopwatchPauseTitle: "暫停模擬",
    stopwatchPlayTitle: "開始模擬",
    stopwatchResetTitle: "重置時間",
    riemannMethodLeft: "左端點",
    riemannMethodMidpoint: "中點",
    riemannMethodRight: "右端點",
    riemannMethodTrapezoidal: "梯形法",
    riemannExpandTitle: "點擊展開黎曼和控制列",
    riemannCollapseTitle: "收起黎曼控制列",
    riemannCloseTitle: "關閉黎曼和",
    riemannFullTitle: "黎曼和",
    riemannDec5Title: "減少 5 個分割",
    riemannDec1Title: "減少 1 個分割",
    riemannInputTitle: "直接輸入分割數 n (2 - 100)",
    riemannInc1Title: "增加 1 個分割",
    riemannInc5Title: "增加 5 個分割",
    sweeperExpandTitle: "點擊展開 y = m 水平掃描控制列",
    sweeperCollapseTitle: "收起 y = m 掃描控制列",
    sweeperCloseTitle: "關閉 y = m 交點分析",
    sweeperFullTitle: "交點分析 y = m",
    sweeperInputTitle: "直接輸入 m 的值",
    sweeperDec05Title: "減少 0.5",
    sweeperDec01Title: "減少 0.1",
    sweeperResetZeroTitle: "重置為 0",
    sweeperInc01Title: "增加 0.1",
    sweeperInc05Title: "增加 0.5",
    insituCircumcircleLabel: "外接圓",
    insituIncircleLabel: "內切圓",
    insituAltitudesLabel: "三條高",
    insituMediansLabel: "中線",
    insituBisectorsLabel: "角平分線",
    insituMeasurementsLabel: "測量",
    insituMidpointLabel: "中點 M",
    insituBisectorLabel: "中垂線 d",
    insituLengthLabel: "長度",
    insituCircleHeader: "圓",
    insituTwoTangentsLabel: (ptId: string) => `2 切線 (${ptId})`,
    insituCreateTangentsLabel: "作兩條切線",
    insituPointLabelPrefix: "點:",
    insituVertexAltitudeTitle: "過此頂點的高線",
    insituVertexMedianTitle: "過此頂點的中線",
    insituVertexBisectorTitle: "過此頂點的角平分線",
    insituPolygonHeader: (count: number) => `多邊形 (${count} 頂點)`,
    insituDeleteElementTitle: "刪除元素",
    insituCloseTitle: "關閉",
    dockCompactTriggerTitle: "開啟教學特徵清單",
    dockCompactTriggerLabel: "特徵",
    dockMenuHeader: "教學特徵",
    dockMoreActionsTitle: "檢視所有其他操作",
    dockMoreActionsLabel: (count: number) => `更多 (${count})`,
    dockAllActionsHeader: "所有操作",
    tangentOutOfDomainBadge: (x0: string) => `x₀ = ${x0} (定義域外)`,
    unmuteVideoLabel: "開啟聲音",
    snapIsoscelesBadge: "等腰",
    emptyFeasibleRegionNotice: "可行域為空",
    feasiblePointMemberBadge: (objValueStr: string) => `✓ 屬於可行域${objValueStr}`,
    feasiblePointViolatedBadge: (expr: string) => `✕ 違反: ${expr}`,
    pedagogicalActions: {
      hatchingLabel: "斜線劃除不可行域",
      hatchingTitle: "以教材斜線樣式標示不等式組區域",
      objectiveOptimizerLabel: "最佳化目標函數 F",
      objectiveOptimizerTitle: "求目標函數 F(x, y) 的最大值與最小值",
      curveIntersectionsLabel: (plotName: string, count: number) =>
        count > 0 ? `${plotName} 的交點 (${count})` : `${plotName} 的交點`,
      curveIntersectionsTitle: (plotName: string) =>
        `求 ${plotName} 與其他圖形的交點及座標軸投影`,
      areaBetweenCurvesLabel: "兩曲線間面積",
      areaBetweenCurvesTitle: "求由兩曲線 f(x) 與 g(x) 所圍成的平面面積",
      conicAxesLabel: (isEllipse: boolean) =>
        isEllipse ? "顯示中心與頂點/軸" : "顯示圓心與半徑",
      conicAxesTitle: "顯示圓錐曲線的中心、頂點與對稱軸",
      conicFociLabel: "顯示焦點",
      conicFociTitle: "顯示橢圓的焦點 F₁, F₂ 與焦距",
      asymptotesLabel: "顯示漸近線",
      asymptotesTitle: "顯示垂直漸近線與水平漸近線",
      symmetryCenterLabel: "對稱中心",
      symmetryCenterTitle: "顯示圖形的對稱中心座標",
      quadraticVertexAxisLabel: "顯示頂點與對稱軸",
      quadraticVertexAxisTitle: "拋物線的頂點座標與對稱軸",
      monotonicityLabel: "單調性與極值",
      monotonicityTitle: "顯示單調區間與極值點",
      inflectionPointLabel: "反曲點",
      inflectionPointTitle: "三次函數圖形的反曲點座標",
      keyFeaturesLabel: "特徵點",
      keyFeaturesTitle: "顯示圖形上的關鍵特徵點",
      curveProbeLabel: "動點 M 座標投影",
      curveProbeTitle: "曲線上可拖曳動點 M 及其在 Ox、Oy 軸上的投影",
      intervalAnalysisLabel: "區間 [a, b] 分析",
      intervalAnalysisTitle: "分析閉區間 [a, b] 上的最大值與最小值（可拖曳端點）",
      rootSweeperLabel: "交點分析 y = m",
      rootSweeperTitle: "利用水平掃描線分析方程式 f(x) = m 的實根個數",
      tangentLineLabel: "x₀ 處切線",
      tangentLineTitle: "作曲線在點 x₀ 處的切線方程式",
      riemannSumLabel: "黎曼和",
      riemannSumTitle: "使用黎曼積分和近似曲線下面積",
      definiteIntegralLabel: "定積分",
      definiteIntegralTitle: "計算函數的定積分值與幾何面積 S",
      absFTransformLabel: "絕對值翻折 |f(x)|",
      absFTransformTitle: "保留 Ox 軸上方部分並將下方部分對稱翻折至上方",
      fAbsTransformLabel: "偶對稱變換 f(|x|)",
      fAbsTransformTitle: "保留 Oy 軸右側部分並對稱複製至左側",
    },
  },
};
